import type { BallotEffect, ErrorCode, GamePhase, Role, SpecialRole, StartIssue } from '@bm/shared';

/** Alle sichtbaren Texte gebündelt an einer Stelle — Grundlage für spätere Mehrsprachigkeit. */
export const de = {
  app: {
    title: 'Bürger Mafia',
    tagline: 'Findet die Mafia. Rettet die Stadt.',
  },
  connection: {
    connecting: 'Verbinde …',
    reconnecting: 'Verbindung wird wiederhergestellt …',
    offline: 'Keine Verbindung',
    retryIn: (seconds: number) => `Nächster Versuch in ${seconds} s`,
    retryNow: 'Jetzt versuchen',
    pending: (count: number) =>
      count === 1
        ? 'Deine Aktion wird gesendet, sobald die Verbindung steht.'
        : `${count} Aktionen werden gesendet, sobald die Verbindung steht.`,
    outdated: 'Neue Version verfügbar — die Seite wird neu geladen.',
    reload: 'Jetzt neu laden',
    takenOver: 'Das Spiel läuft in einem anderen Tab.',
    resumeHere: 'Hier fortsetzen',
  },
  roomClosed: {
    KICKED: 'Du wurdest aus dem Raum entfernt.',
    ROOM_GONE: 'Dieser Raum existiert nicht mehr.',
    CLOSED: 'Der Host hat den Raum geschlossen.',
  },
  rejoin: {
    cta: 'Zurück ins Spiel',
    hint: 'Handy gewechselt oder Verlauf gelöscht? Such deinen Platz über deinen Namen.',
    namePlaceholder: 'Dein Name im Spiel',
    search: 'Platz suchen',
    noMatch: 'Kein freier Platz mit diesem Namen gefunden.',
    optionSubtitle: (players: number, phase: string) => `${players} Spieler · ${phase}`,
    request: 'Wiedereinstieg anfragen',
    waiting: 'Warte auf Bestätigung aus der Runde …',
    waitingHint: 'Jemand im Spiel muss dich hereinlassen. Sag am besten kurz Bescheid.',
    cancel: 'Anfrage abbrechen',
    denied: 'Deine Anfrage wurde abgelehnt.',
    expired: 'Deine Anfrage ist abgelaufen.',
    approved: 'Willkommen zurück!',
    promptTitle: (name: string) => `„${name}" möchte zurück ins Spiel.`,
    promptWarning:
      'Wer hereingelassen wird, sieht die Geheimrolle dieses Platzes. Nur zulassen, wenn du sicher bist, dass es wirklich diese Person ist.',
    approve: 'Zulassen',
    deny: 'Ablehnen',
  },
  common: {
    back: '← Zurück',
    cancel: 'Abbrechen',
    leaveRoomConfirm: 'Raum wirklich verlassen?',
    leaveRoomBody: 'Dein Platz bleibt kurz frei — du kannst über „Zurück ins Spiel" wieder rein.',
    leaveRoomConfirmCta: 'Verlassen',
    closeRoom: 'Raum schließen',
    closeRoomConfirm: 'Raum für alle schließen?',
    closeRoomBody:
      'Der Raum wird aufgelöst und alle landen auf der Startseite. Ein laufendes Spiel ist damit beendet.',
    closeRoomConfirmCta: 'Schließen',
    menu: 'Menü',
    myRole: 'Deine Rolle',
    myRoleHint: 'Halte die Karte gedrückt — losgelassen ist sie sofort wieder verdeckt.',
    soundOn: 'Ton einschalten',
    soundOff: 'Ton ausschalten',
    timerLabel: (what: string, seconds: number) =>
      `${what}: noch ${seconds} ${seconds === 1 ? 'Sekunde' : 'Sekunden'}`,
    timerRoleReveal: 'Zeit für die Rollenanzeige',
    timerDiscussion: 'Verbleibende Diskussionszeit',
    timerVoting: 'Verbleibende Abstimmungszeit',
    timerTieBreaker: 'Verbleibende Zeit bis zur Stichwahl',
  },
  crash: {
    title: 'Da ist etwas abgestürzt.',
    hint: 'Lade die Seite neu — dein Platz im Raum bleibt erhalten.',
    reload: 'Neu laden',
  },
  rules: {
    title: 'Wie es funktioniert',
    open: 'Regeln',
    goalTitle: 'Ziel',
    goalCitizens: 'Bürger gewinnen, wenn alle Mafiosi hinausgewählt sind.',
    goalMafia:
      'Die Mafia gewinnt, wenn sie genauso viele ist wie die restlichen Bürger — oder wenn sich die Stadt zu oft nicht einigt.',
    flowTitle: 'Eine Runde',
    flowSteps: [
      'Jeder deckt heimlich seine Rolle auf. Die Mafia sieht dabei ihre Kollegen.',
      'Diskussion: Redet miteinander und findet heraus, wer lügt.',
      'Abstimmung: Alle wählen gleichzeitig. Die eigene Stimme lässt sich nicht mehr ändern.',
      'Wer die meisten Stimmen hat, scheidet aus — und die Rolle wird aufgedeckt.',
      'Bei Gleichstand gibt es eine Stichwahl.',
    ],
    rolesTitle: 'Sonderrollen in dieser Runde',
    rolesNone: 'In dieser Runde sind keine Sonderrollen aktiv.',
  },
  home: {
    createRoom: 'Raum erstellen',
    localMode: 'Nur Rollen verteilen',
    localModeHint: 'Ein Handy für alle: Namen eintragen, weiterreichen, fertig.',
    openRooms: 'Offene Spiele',
    noRooms: 'Gerade ist kein Raum offen. Erstelle den ersten!',
    loadingRooms: 'Offene Räume werden geladen …',
    players: (count: number, max: number) => `${count} / ${max} Spieler`,
    mafiaCount: (count: number) => `${count} Mafia`,
    join: 'Beitreten',
    locked: 'Gesperrt',
  },
  nameEntry: {
    title: 'Wie möchtest du heißen?',
    placeholder: 'Dein Name',
    join: 'Beitreten',
    create: 'Raum erstellen',
  },
  lobby: {
    players: (count: number) => `${count} Spieler`,
    settings: 'Einstellungen',
    mafiaCount: 'Anzahl Mafia',
    startCountdown: 'Start-Countdown',
    discussion: 'Diskussionszeit',
    voting: 'Abstimmungszeit',
    tieBreaker: 'Tie-Breaker-Zeit',
    maxTieRounds: 'Maximale Unentschieden-Runden',
    allowAbstain: '„Enthalten" erlauben',
    lockRoom: 'Raum sperren',
    kick: 'Entfernen',
    start: 'Spiel starten',
    waitingForHost: 'Warten auf den Host …',
    host: 'Host',
    you: 'Du',
    offline: 'Offline',
    startIssue: (issue: StartIssue, needed: number, have: number): string => {
      switch (issue) {
        case 'NOT_ENOUGH_PLAYERS':
          return `Noch ${Math.max(0, needed - have)} Spieler nötig`;
        case 'INVALID_MAFIA_COUNT':
          return 'Mafia-Anzahl passt nicht zur Spielerzahl';
        case 'TOO_MANY_SPECIAL_ROLES':
          return `Zu viele Sonderrollen für ${have} Spieler`;
      }
    },
    soundOffer: 'Ton einschalten? Signalisiert Phasenwechsel, wenn das Handy auf dem Tisch liegt.',
    soundOfferCta: 'Einschalten',
    soundOfferDismiss: 'Hinweis ausblenden',
    compositionMafia: (count: number) => `${count} Mafia`,
    /** „Bürger" ist im Deutschen im Singular wie im Plural gleich. */
    compositionCitizens: (count: number) => `${count} Bürger`,
    specialRoles: 'Sonderrollen',
    specialRolesHint: (max: number) =>
      max === 0
        ? 'Für Sonderrollen fehlen noch Spieler.'
        : `Höchstens ${max} bei dieser Spielerzahl.`,
    leave: 'Raum verlassen',
  },
  countdown: {
    title: 'Das Spiel beginnt in …',
  },
  roleReveal: {
    holdToReveal: 'Zum Aufdecken gedrückt halten',
    youAre: 'DU BIST',
    citizenHint: 'Finde die Mafia.',
    mafiaHint: 'Deine Mafia-Kollegen:',
    mafiaSoloHint: 'Du bist der einzige Mafioso. Handle allein und tarne dich gut.',
    continue: 'Weiter',
    waitingFor: (done: number, total: number) => `${done} / ${total} bereit`,
  },
  discussion: {
    title: (round: number) => `RUNDE ${round}`,
    hint: 'Diskutiert und findet die Mafia.',
    startVoting: 'Abstimmung starten',
  },
  voting: {
    title: 'Wen möchtest du rauswählen?',
    tieBreakerTitle: 'Stichwahl',
    abstain: 'Enthalten',
    submit: 'Stimme abgeben',
    selected: 'AUSGEWÄHLT',
    submitted: 'STIMME ABGEGEBEN',
    waiting: 'Warte auf die anderen Spieler …',
    progress: (voted: number, total: number) => `${voted} / ${total} haben gewählt`,
    hasVoted: 'Hat gewählt',
    stillVoting: 'Wählt noch',
    confirmTitle: (name: string) => `Für ${name} stimmen?`,
    confirmAbstainTitle: 'Enthalten?',
    confirmBody: 'Die Stimme lässt sich danach nicht mehr ändern.',
    confirmCta: 'Stimme abgeben',
    spectating: 'Du bist ausgeschieden und schaust zu.',
    abilityOn: (name: string) => `Fähigkeit eingesetzt auf ${name}`,
  },
  roundResult: {
    counting: 'Die Stimmen werden ausgezählt …',
    eliminated: (name: string) => `${name} wurde aus dem Spiel gewählt.`,
    wasRole: (role: string) => `war ${role}`,
    noElimination: 'NIEMAND WURDE ELIMINIERT',
    protectedHint: 'Jemand stand unter Schutz.',
    tie: 'UNENTSCHIEDEN',
    tieHint: (names: string) => `${names} haben jeweils die meisten Stimmen.`,
    tieCounter: (count: number, max: number) => `Unentschieden ${count} / ${max}`,
    votesLabel: (count: number) => `${count} ${count === 1 ? 'Stimme' : 'Stimmen'}`,
  },
  gameOver: {
    mafiaWins: 'MAFIA GEWINNT',
    citizensWin: 'BÜRGER GEWINNEN',
    mafiaWinsHint: 'Die Mafia hat die Kontrolle über die Stadt übernommen.',
    citizensWinHint: 'Alle Mafia-Mitglieder wurden enttarnt.',
    mafiaWinsMaxTies: 'Die Stadt konnte sich nicht einigen — die Mafia gewinnt.',
    history: 'Spielhistorie',
    newRound: 'Neue Runde',
    mafiaLabel: 'MAFIA',
    citizensLabel: 'BÜRGER',
  },
  history: {
    title: 'Spielhistorie',
    round: (n: number) => `Runde ${n}`,
    tieBreaker: 'Tie-Breaker',
    votes: 'Stimmen',
    tally: 'Auszählung',
    effects: 'Was die Auszählung verändert hat',
    noElimination: 'Niemand eliminiert',
    empty: 'Noch keine abgeschlossene Runde.',
  },
  local: {
    setupTitle: 'Wer spielt mit?',
    namePlaceholder: 'Name eingeben',
    add: 'Hinzufügen',
    remove: (name: string) => `${name} entfernen`,
    duplicate: 'Diesen Namen gibt es schon.',
    full: (max: number) => `Mehr als ${max} Spieler gehen nicht.`,
    playerCount: (count: number, max: number) => `${count} / ${max} Spieler`,
    mafiaCount: 'Anzahl Mafia',
    deal: 'Rollen verteilen',
    handoffTitle: 'Gib das Handy weiter an',
    handoffConfirm: (name: string) => `Ich bin ${name}`,
    handoffProgress: (position: number, total: number) => `Spieler ${position} von ${total}`,
    handoffHint: 'Erst antippen, wenn du das Handy in der Hand hast.',
    seen: 'Gesehen — weitergeben',
    doneTitle: 'Alle kennen ihre Rolle',
    doneHint: 'Legt das Handy weg und spielt los.',
    doneSummary: (players: number, mafia: number) =>
      `${players} Spieler · ${mafia} ${mafia === 1 ? 'Mafioso' : 'Mafiosi'}`,
    redeal: 'Neu verteilen',
    exit: 'Zurück zum Start',
    abortConfirm: 'Rollenverteilung wirklich abbrechen?',
    abortConfirmCta: 'Abbrechen und zurück',
  },
  roles: {
    CITIZEN: 'Bürger',
    MAFIA: 'Mafia',
    BLESSING: 'Schutzengel',
    VOTE_BLOCK: 'Saboteur',
    DEAD_REDEMPTION: 'Letztes Wort',
    DOUBLE_UP: 'Doppelstimme',
  } satisfies Record<Role, string>,
  /** Texte der Sonderrollen — Lobby-Schalter, Rollenkarte und Einsatz-Dialog. */
  specialRoles: {
    BLESSING: {
      emoji: '🛡️',
      lobbyHint: 'Bewahrt einmal im Spiel eine andere Person vor dem Rauswurf.',
      cardHint:
        'Einmal im Spiel kannst du jemanden beschützen. Bekommt diese Person die meisten Stimmen, wird niemand eliminiert.',
      useCta: 'Jemanden beschützen',
      pickTitle: 'Wen möchtest du beschützen?',
      activeLabel: 'Schutz aktiv',
    },
    VOTE_BLOCK: {
      emoji: '🤫',
      lobbyHint: 'Lässt einmal im Spiel heimlich die Stimme einer anderen Person verstummen.',
      cardHint:
        'Einmal im Spiel kannst du eine Stimme verstummen lassen. Niemand merkt es — auch der Betroffene nicht.',
      useCta: 'Stimme blockieren',
      pickTitle: 'Wessen Stimme soll verstummen?',
      activeLabel: 'Blockade aktiv',
    },
    DEAD_REDEMPTION: {
      emoji: '🕯️',
      lobbyHint: 'Darf nach dem eigenen Ausscheiden noch einmal mitstimmen.',
      cardHint:
        'Wirst du hinausgewählt, bleibt dir eine Stimme. Du entscheidest, in welcher Runde du sie einsetzt.',
      useCta: 'Aus dem Jenseits mitstimmen',
      pickTitle: 'Diese Runde mitstimmen?',
      activeLabel: 'Stimmrecht aktiv',
    },
    DOUBLE_UP: {
      emoji: '✌️',
      lobbyHint: 'Lässt die eigene Stimme einmal im Spiel doppelt zählen.',
      cardHint: 'Einmal im Spiel zählt deine Stimme doppelt. Wähle den Moment gut.',
      useCta: 'Stimme verdoppeln',
      pickTitle: 'Stimme in dieser Runde verdoppeln?',
      activeLabel: 'Doppelstimme aktiv',
    },
  } satisfies Record<
    SpecialRole,
    {
      emoji: string;
      lobbyHint: string;
      cardHint: string;
      useCta: string;
      pickTitle: string;
      activeLabel: string;
    }
  >,
  /** Anonyme Erklärung, warum Stimmenliste und Auszählung auseinandergehen. */
  ballotEffects: {
    PROTECTION: 'Jemand stand unter Schutz.',
    VOTE_BLOCKED: 'Eine Stimme wurde blockiert und nicht gezählt.',
    VOTE_DOUBLED: 'Eine Stimme zählte doppelt.',
    DEAD_VOTE: 'Eine Stimme kam von jemandem, der nicht mehr im Spiel ist.',
  } satisfies Record<BallotEffect, string>,
  phases: {
    LOBBY: 'Lobby',
    STARTING: 'Start',
    ROLE_REVEAL: 'Rollen',
    DISCUSSION: 'Diskussion',
    VOTING: 'Abstimmung',
    TIE_BREAKER: 'Stichwahl',
    ROUND_RESULT: 'Ergebnis',
    GAME_OVER: 'Spielende',
  } satisfies Record<GamePhase, string>,
  errors: {
    BAD_MESSAGE: 'Da ist etwas schiefgelaufen.',
    RATE_LIMITED: 'Bitte etwas langsamer.',
    PROTOCOL_MISMATCH: 'Bitte lade die Seite neu.',
    SERVER_LIMIT: 'Der Server ist gerade ausgelastet.',
    ROOM_NOT_FOUND: 'Diesen Raum gibt es nicht mehr.',
    ROOM_FULL: 'Der Raum ist voll.',
    ROOM_LOCKED: 'Dieser Raum ist gesperrt.',
    GAME_RUNNING: 'Das Spiel läuft bereits.',
    ALREADY_IN_ROOM: 'Du bist bereits in einem Raum.',
    NOT_IN_ROOM: 'Du bist in keinem Raum.',
    INVALID_NAME: 'Bitte gib einen Namen ein.',
    NAME_TAKEN: 'Dieser Name wird bereits verwendet.',
    NOT_HOST: 'Nur der Host darf das.',
    NOT_ENOUGH_PLAYERS: 'Mindestens 3 Spieler werden benötigt.',
    INVALID_MAFIA_COUNT: 'Diese Mafia-Anzahl passt nicht zur Spielerzahl.',
    TOO_MANY_SPECIAL_ROLES: 'Für so viele Sonderrollen fehlen Spieler.',
    INVALID_SETTINGS: 'Diese Einstellung ist ungültig.',
    CANNOT_KICK_SELF: 'Du kannst dich nicht selbst entfernen.',
    PLAYER_NOT_FOUND: 'Dieser Spieler wurde nicht gefunden.',
    SEAT_ACTIVE: 'Dieser Platz ist gerade besetzt.',
    REJOIN_NOT_ALLOWED: 'Das darf gerade jemand anderes entscheiden.',
    REQUEST_NOT_FOUND: 'Diese Anfrage gibt es nicht mehr.',
    TOO_MANY_REQUESTS: 'Zu viele offene Anfragen. Bitte kurz warten.',
    WRONG_PHASE: 'Das geht gerade nicht.',
    NOT_ALIVE: 'Ausgeschiedene Spieler können nicht mehr abstimmen.',
    ALREADY_VOTED: 'Du hast bereits abgestimmt.',
    INVALID_TARGET: 'Diese Wahl ist nicht möglich.',
    SELF_VOTE: 'Du kannst dich nicht selbst wählen.',
    ABSTAIN_DISABLED: 'Enthaltung ist deaktiviert.',
    ABILITY_UNAVAILABLE: 'Diese Fähigkeit steht dir gerade nicht zur Verfügung.',
    INVALID_ABILITY_TARGET: 'Dieses Ziel ist nicht möglich.',
  } satisfies Record<ErrorCode, string>,
};
